import { create } from "zustand";
import type { Session, User, Subscription } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";

interface AuthState {
  session: Session | null;
  user: User | null;
  initialized: boolean;
  // Held so repeat hydrate calls are idempotent and the listener can be torn down (HMR/tests).
  _authSub: Subscription | null;
  hydrate: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  session: null,
  user: null,
  initialized: false,
  _authSub: null,
  hydrate: async (): Promise<void> => {
    if (get().initialized || get()._authSub !== null) return;
    // Register listener BEFORE awaiting getSession so events firing in the gap aren't lost.
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      set({
        session,
        user: session?.user ?? null,
        initialized: true,
      });
    });
    set({ _authSub: subscription });

    const { data } = await supabase.auth.getSession();
    // A newer auth event wins over a slower initial getSession response.
    if (!get().initialized) {
      set({ session: data.session, user: data.session?.user ?? null, initialized: true });
    }
  },
  signIn: async (email, password): Promise<void> => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      throw error;
    }
    set({ session: data.session, user: data.user });
  },
  signOut: async (): Promise<void> => {
    const { error } = await supabase.auth.signOut();
    if (error) {
      throw error;
    }
    set({ session: null, user: null });
  },
}));
